// Lightweight Web Audio synth: ambient pad + SFX. No asset files.
let ctx = null;
let master = null;
let ambientGain = null;
let ambientTimer = null;
let currentTheme = "dark";
let started = false;

function muted() {
  return localStorage.getItem("muted") === "1";
}

function ensure() {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  master = ctx.createGain();
  master.gain.value = muted() ? 0 : 0.5;
  master.connect(ctx.destination);
  ambientGain = ctx.createGain();
  ambientGain.gain.value = 0.12;
  ambientGain.connect(master);
  return ctx;
}

function note(freq, dur, type = "sine", gain = 0.3, dest = null) {
  if (!ctx) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.value = freq;
  g.gain.setValueAtTime(0, ctx.currentTime);
  g.gain.linearRampToValueAtTime(gain, ctx.currentTime + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
  o.connect(g);
  g.connect(dest || master);
  o.start();
  o.stop(ctx.currentTime + dur + 0.05);
}

const SCALES = {
  // playful bright: major pentatonic
  light: [523.25, 587.33, 659.25, 783.99, 880.0, 1046.5],
  // cyberpunk: minor / darker
  dark: [220.0, 261.63, 293.66, 329.63, 392.0, 440.0],
};

function scheduleAmbient() {
  clearInterval(ambientTimer);
  const isLight = currentTheme === "light";
  const scale = SCALES[currentTheme] || SCALES.dark;
  const tempo = isLight ? 1400 : 2200;
  ambientTimer = setInterval(() => {
    if (!ctx || muted()) return;
    const f = scale[Math.floor(Math.random() * scale.length)];
    const timbre = isLight ? "triangle" : "sawtooth";
    note(f, isLight ? 1.6 : 2.6, timbre, isLight ? 0.18 : 0.1, ambientGain);
    if (!isLight && Math.random() < 0.5) note(f / 2, 3.2, "sine", 0.08, ambientGain);
  }, tempo);
}

export function initAudio() {
  const c = ensure();
  if (!c) return;
  if (c.state === "suspended") c.resume();
  if (!started && !muted()) {
    started = true;
    scheduleAmbient();
  }
}

export function setSoundTheme(theme) {
  currentTheme = theme;
  if (started && !muted()) scheduleAmbient();
}

export function toggleMute() {
  const m = !muted();
  localStorage.setItem("muted", m ? "1" : "0");
  if (master && ctx) master.gain.setTargetAtTime(m ? 0 : 0.5, ctx.currentTime, 0.05);
  if (m) {
    clearInterval(ambientTimer);
    started = false;
  } else {
    initAudio();
  }
  return m;
}

export function isMuted() {
  return muted();
}

export function playSfx(name) {
  if (!ctx || muted()) return;
  if (ctx.state === "suspended") ctx.resume();
  const light = currentTheme === "light";
  switch (name) {
    case "coin":
      note(880, 0.09, "square", 0.25);
      setTimeout(() => note(1318, 0.12, "square", 0.22), 70);
      break;
    case "win":
      [523, 659, 784, 1046].forEach((f, i) => setTimeout(() => note(f, 0.22, light ? "triangle" : "sawtooth", 0.22), i * 100));
      break;
    case "lose":
      note(300, 0.4, "sawtooth", 0.2);
      setTimeout(() => note(180, 0.5, "sawtooth", 0.18), 120);
      break;
    case "pop":
      note(660, 0.12, "sine", 0.25);
      break;
    case "click":
      note(440, 0.05, "square", 0.12);
      break;
    case "hit":
      note(160, 0.12, "sawtooth", 0.28);
      setTimeout(() => note(110, 0.14, "square", 0.2), 40);
      break;
    case "power":
      note(220, 0.08, "sawtooth", 0.28);
      setTimeout(() => note(440, 0.1, "square", 0.26), 60);
      setTimeout(() => note(880, 0.16, "square", 0.24), 130);
      break;
    default:
      break;
  }
}

// ---- Sound packs: each robot type gets a signature chirp + victory jingle ----
function hashType(type) {
  let h = 0;
  for (let i = 0; i < (type || "bot").length; i++) h = (h * 31 + type.charCodeAt(i)) & 0xffff;
  return h;
}
const CHIRP_SCALE = [523.25, 587.33, 659.25, 783.99, 880.0, 987.77, 1046.5, 1174.66];
const TIMBRES = ["sine", "triangle", "square", "sawtooth"];

export function playChirp(type) {
  if (!ctx || muted()) return;
  if (ctx.state === "suspended") ctx.resume();
  const h = hashType(type);
  const timbre = TIMBRES[h % TIMBRES.length];
  const a = CHIRP_SCALE[h % CHIRP_SCALE.length];
  const b = CHIRP_SCALE[(h >> 3) % CHIRP_SCALE.length];
  const c = CHIRP_SCALE[(h >> 6) % CHIRP_SCALE.length];
  [a, b, c].forEach((f, i) => setTimeout(() => note(f, 0.14, timbre, 0.2), i * 90));
}

export function playJingle(type) {
  if (!ctx || muted()) return;
  if (ctx.state === "suspended") ctx.resume();
  const h = hashType(type);
  const timbre = TIMBRES[h % TIMBRES.length];
  const root = 392.0 + (h % 5) * 20;
  const seq = [root, root * 1.25, root * 1.5, root * 2, root * 1.5];
  seq.forEach((f, i) => setTimeout(() => note(f, 0.2, timbre, 0.22), i * 110));
}
